import type {UseQueryResult} from '@tanstack/react-query'
import type {ParserT} from '@freckle/parser'
import {
  Parser,
  string,
  record,
  nullableDefined,
  nullableDefault,
  array,
  field
} from '@freckle/parser'
import {useAsyncOnceDefined} from '@freckle/educator-entities/ts/common/hooks/use-async'
import {fancyClient, extractData} from '@freckle/educator-entities/ts/openapi-clients'
import {isManagedByRenaissance} from '@freckle/educator-entities/ts/roster/identity-provider'
import {useTeacher} from './use-teacher'

const QUERY_KEY = 'fetchRenaissanceJwt'

export function useJwt(): UseQueryResult<Jwt | null> {
  const teacher = useTeacher()
  const jwt = useAsyncOnceDefined(
    ({teacher}) =>
      // Don't waste time/bandwidth unless we know the user may have a JWT
      isManagedByRenaissance(teacher) ? fetchRenaissanceJwt() : Promise.resolve(null),
    QUERY_KEY,
    [teacher.data?.id],
    {teacher}
  )
  return jwt
}

export type RenaissanceAppChild = Omit<RenaissanceApp, 'children'>

const renaissanceAppChildParser: ParserT<RenaissanceAppChild> = record({
  appCode: string(),
  displayName: string(),
  imageOverrideUrl: nullableDefined(string()),
  deepLink: nullableDefined(string())
})

// See: https://github.com/RenaissancePlace/element-RenaissanceHeader/blob/main/projects/renaissance-header/src/app/models/app-nav-v2.model.ts
export type RenaissanceApp = {
  appCode: string
  displayName: string
  imageOverrideUrl: null | string
  deepLink: null | string
  children: null | RenaissanceAppChild[]
}

const renaissanceAppParser: ParserT<RenaissanceApp> = record({
  appCode: string(),
  displayName: string(),
  imageOverrideUrl: nullableDefined(string()),
  deepLink: nullableDefined(string()),
  children: nullableDefined(array(renaissanceAppChildParser))
})

export type Jwt = {
  homeuri: string
  appnav_v2: RenaissanceApp[]
}

const jwtParser: ParserT<Jwt | null> = nullableDefined(
  record({
    homeuri: field(string(), 'homeuri'),
    appnav_v2: field(nullableDefault(array(renaissanceAppParser), []), 'appnav_v2')
  })
)

const parse = Parser.mkRun(jwtParser)

const fetchRenaissanceJwt = (): Promise<Jwt | null> =>
  fancyClient.GET('/3/renaissance/jwt').then(extractData).then(parse)
